import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { Col, Grid, Row } from 'antd';

import ImageC from 'src/components/Image';
import { Title } from 'src/components/Layout/Typography';
import g from 'src/constants/global';
import { Articles, BannerItem, ICategoryByGroup, PromoProps } from 'src/interface';

import Advertising from './Advertising';
import CardArticle from './CardArticle';
import MainArticle from './MainArticle';
import TabMenu from './TabMenu';

type Props = {
	categoryTopic: ICategoryByGroup[];
	articles: Articles[];
	ads?: PromoProps[];
	activeKey: string;
	onChange: (e: any) => void;
	totalArticle?: number;
	isNoneSectionTitle?: boolean;
	showLoadmore?: boolean;
	showMainArticle?: boolean;
	color?: string;
	isSetHeight?: boolean;
	bannerAd?: BannerItem;
};

const GPT = dynamic(() => import('src/components/GPT'), { ssr: false });
const Article = (props: Props) => {
	const {
		activeKey,
		totalArticle,
		categoryTopic,
		showLoadmore,
		isNoneSectionTitle,
		articles,
		ads,
		onChange,
		bannerAd,
	} = props;
	const [mounted, setMounted] = useState(false);
	useEffect(() => {
		setMounted(true);
	}, []);

	const { xs, md, lg, xl } = Grid.useBreakpoint();
	const mdS = mounted && md ? 5 : 50;
	const lgS = mounted && lg ? 6 : mdS;
	const keyTopic = mounted && xl ? 7 : lgS;
	const showLoadmoreActive = showLoadmore ?? true;
	const dataCategoryTopic = categoryTopic || [];
	const activeKeySlug = dataCategoryTopic[Number(activeKey?.split('__')[0]) - 1]?.full_slug;
	const limit = ['pilihan', 'latest'].includes(activeKey?.split('__')[1]) ? totalArticle : 4;

	const categoryTopics = useMemo(() => {
		return [
			...dataCategoryTopic,
			{ name: 'Semua Kategori', full_slug: 'semua-kategori', slug: 'semua-kategori' },
		];
	}, [dataCategoryTopic]);

	return (
		<div className="mt-6.5 pb-7.75 sm:mt-8.75 sm:pb-11 lg:mt-10.25 lg:pb-23.75 container">
			{!isNoneSectionTitle && (
				<>
					<h2 className="mb-1 md:mb-2 lg:mb-3 text-lg sm:text-xl md:text-2xl font-bold">
						Baca Artikel Kesehatan
					</h2>
					<p className='mb-6 lg:mb-10 text-[12px] sm:text-[14px] md:text-[16px] font-normal text-neutral-5'>
						Informasi kesehatan tepercaya hanya untukmu
					</p>
					{lg && (
						<div>
							<GPT
								name="gpt-ad-homepage-leaderboard"
								adUnit={g.AD.HOMEPAGE_LEADERBOARD_UNIT}
								size={g.AD.HOMEPAGE_LEADERBOARD_SIZE}
								target={[['channel', 'homepage']]}
								style={{ overflow: 'hidden', marginTop: -6, marginBottom: 32 }}
							/>
						</div>
					)}
					{xs && (
						<GPT
							name="gpt-ad-homepage-halfbanner"
							adUnit={g.AD.HOMEPAGE_MOBILE_HALFBANNER_UNIT}
							size={g.AD.HOMEPAGE_MOBILE_HALFBANNER_SIZE}
							target={[['channel', 'homepage']]}
							style={{ marginBottom: 22 }}
						/>
					)}
				</>
			)}

			<TabMenu
				activeKey={activeKey}
				onChange={onChange}
				categoryTopics={categoryTopics}
				keyTopic={keyTopic}
			/>

			<Row gutter={[24, 8]}>
				<Col xs={24} sm={24} md={24} lg={16} xl={16}>
					<MainArticle data={articles?.[0]} subCategory={activeKeySlug} />

					<div className='py-4'>
						<CardArticle data={articles} subCategory={activeKeySlug} length={limit} />
					</div>

					{showLoadmoreActive && (
						<Link href={activeKeySlug || '/'} passHref className="flex justify-center items-center cursor-pointer mt-12.5 mb-5">
							<p className="mb-0 mr-3 font-semibold text-primary">Lihat lebih banyak</p>
							<ImageC
								alt="arrow-right-short"
								src="/icons/arrow-right-short.svg"
								height={xs ? 24 : 32}
								width={xs ? 24 : 32}
							/>
						</Link>
					)}
				</Col>

				{lg && (
					<Col span={8}>
						<Advertising
							ads={ads}
							adSource="homepage"
							adTarget={[['channel', ['homepage']]]}
							adUnits={[g.AD.HOMEPAGE_HALFPAGE_UNIT, g.AD.HOMEPAGE_MEDIUMREC_UNIT]}
							sizes={[g.AD.HOMEPAGE_HALFPAGE_SIZE, g.AD.HOMEPAGE_MEDIUMREC_SIZE]}
							bannerAd={bannerAd}
						/>
					</Col>
				)}
			</Row>
		</div>
	);
};

export default Article;
